import PropTypes from 'prop-types';
import { includes, find, path, propEq } from 'ramda';
import { connect } from 'react-redux';
import { readEndpoint } from 'redux-json-api';
import { createSelector } from 'reselect';
import React, { Component } from 'react';


import Avatar from '@material-ui/core/Avatar';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import grey from '@material-ui/core/es/colors/grey';
import { Person } from '@material-ui/icons';

import { readEndpointDedup } from '../../modules/reducers/api';

const styles = {
  message: {
    name: {
      color: 'rgba(0, 0, 0, 0.87)', // darkBlack from material-ui
    },
  },
  datetime: {
    fontSize: 12,
    color: grey[700],
  },
};


class RideMessage extends Component {
  static propTypes = {
    // own props
    message: PropTypes.shape({
      type: PropTypes.oneOf(['dashboard-message']).isRequired,
      id: PropTypes.string.isRequired,
      attributes: PropTypes.shape({
        'sender-type-name': PropTypes.oneOf(['ride']).isRequired,
        'sender-id': PropTypes.number.isRequired,
        'payload': PropTypes.shape({}),
      }).isRequired,
    }).isRequired,
    routeBlockageNames: PropTypes.shape({}).isRequired,

    // state props
    company: PropTypes.shape({}),
    driver: PropTypes.shape({}),
    ride: PropTypes.shape({}),

    // dispatch props
    readEndpointDedup: PropTypes.func.isRequired,
  };

  componentDidMount() {
    const { message, ride, company, readEndpointDedup } = this.props;

    // Only load our ride when we are a CREATE or UPDATE ride message.
    if (message.attributes.action === 'CREATE' ||
        message.attributes.action === 'UPDATE') {
      // The ride endpoint returns the driver as well, but not the company.
      if (!ride)
        readEndpointDedup(`ride/${message.attributes['sender-id']}`)
          .catch((error) => {
            /**
             * Only report errors when they're not about missing rides.
             */
            if (!error.response ||
                !error.response.status ||
                error.response.status !== 404) {
              console.error(`failed to read \`ride/${message.attributes['sender-id']}\`: ${error}`);
            }
          });
      else if (!company) readEndpointDedup(`company/${ride.attributes['company-id']}`);
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      company, message, ride,
      readEndpointDedup,
    } = this.props;

    const {
      company: nextCompany, message: nextMessage, ride: nextRide,
    } = nextProps;

    /**
     * Read in the company associated with the new ride prop, when it doesn't
     * exist in redux state yet.
     */
    if (includes(message.attributes.action, ['CREATE', 'UPDATE']) ||
        includes(nextMessage.attributes.action, ['CREATE', 'UPDATE'])) {
      if (!ride && nextRide && !nextCompany)
        readEndpointDedup(`company/${nextRide.attributes['company-id']}`);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Never update when not a CREATE or UPDATE ride message.
    if (this.props.message.attributes.action !== 'CREATE' &&
        this.props.message.attributes.action !== 'UPDATE')
      return false;

    // Only update if a previously unavailable ride, company or driver have
    // loaded.
    if (!this.props.ride && nextProps.ride) return true;
    if (!this.props.company && nextProps.company) return true;
    if (!this.props.driver && nextProps.driver) return true;

    return false;
  };

  render() {
    const { message, routeBlockageNames, ride, company, driver } = this.props;

    if ((
      message.attributes.action !== 'CREATE' &&
      message.attributes.action !== 'UPDATE'
    ) || (
      message.attributes.action === 'UPDATE' && (
        path(['attributes', 'payload', 'update-type'], message) !== 'shuttle-started' &&
        path(['attributes', 'payload', 'update-type'], message) !== 'shuttle-ended'
      )
    )) {
      return null;
    }

    if (!ride || !company || !driver) {
      return (
        <ListItem alignItems="flex-start">
          <ListItemText inset primary="Laden..." />
        </ListItem>
      )
    }

    const routeBlockageId = path(['relationships', 'routeblockage', 'data', 'id'], ride);
    const routeBlockageName = routeBlockageNames[routeBlockageId] || 'Onbekend';
    const companyName = path(['attributes', 'name'], company) || 'Onbekend';
    const name = (path(['attributes', 'first-name'], driver) || '') + ' ' +
      (path(['attributes', 'last-name'], driver) || '');
    const datetime = new Date(path(['attributes', 'created-at'], message));

    let title = 'Bus onderweg';

    if (message.attributes.action === 'UPDATE') {
      title = 'Rit bijgewerkt';

      if (path(['attributes', 'payload', 'update-type'], message) === 'shuttle-started')
        title = 'Bus rijdt op stremming';
      else if (path(['attributes', 'payload', 'update-type'], message) === 'shuttle-ended')
        title = 'Bus onderweg naar stalling';
    }

    return (
      <ListItem alignItems="flex-start">
        <ListItemAvatar>
          <Avatar>
            <Person />
          </Avatar>
        </ListItemAvatar>

        <ListItemText
          primary={
            <span>
              {title}<br />
              <span style={styles.datetime}>{datetime.toLocaleString()}</span>
            </span>
          }
          secondary={
            <span style={{ margin: 0 }}>
              <span style={styles.message.name}>{companyName}</span> --
              Op stremming `{routeBlockageName}`.
            </span>
          }
        />
      </ListItem>
    );
  };
}


const selectRideId = (state, ownProps) => ownProps.message.attributes['sender-id'];
const selectRides = state => path(['api', 'ride', 'data'], state) || [];
const selectCompanies = state => path(['api', 'company', 'data'], state) || [];
const selectApiUsers = state => path(['api', 'api-user', 'data'], state) || [];

const makeGetRide = () => {
  return createSelector([selectRideId, selectRides], (rideId, rides) => {
    return find(propEq('id', String(rideId)))(rides);
  });
};

const makeGetCompany = () => {
  return createSelector([selectRideId, selectRides, selectCompanies], (rid, rs, cs) => {
    const r = find(propEq('id', String(rid)))(rs);
    const cid = path(['attributes', 'company-id'], r);

    return find(propEq('id', String(cid)))(cs);
  });
};

const makeGetDriver = () => {
  return createSelector([selectRideId, selectRides, selectApiUsers], (rideId, rides, apiUsers) => {
    const ride = find(propEq('id', String(rideId)))(rides);
    const driverId = path(['relationships', 'driver', 'data', 'id'], ride);

    return find(propEq('id', String(driverId)))(apiUsers);
  });
};


const makeMapStateToProps = () => {
  const getRide = makeGetRide();
  const getCompany = makeGetCompany();
  const getDriver = makeGetDriver();

  return (state, ownProps) => {
    return {
      company: getCompany(state, ownProps),
      driver: getDriver(state, ownProps),
      ride: getRide(state, ownProps),
    };
  };
};

const mapDispatchToProps = {
  readEndpointDedup,
};

export default connect(makeMapStateToProps, mapDispatchToProps)(RideMessage);
